import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { InvestigationAiRoleStrip } from '../../investigations/components/InvestigationAiRole';
import { InvestigationKnowledgeSection } from '../../investigations/components/InvestigationKnowledgeSection';
import { InvestigationSynthesisCard } from '../../investigations/components/InvestigationSynthesisCard';
import type { InvestigationSynthesisDto } from '../../investigations/types/investigation';
import { TourEvidencePanel, TourEvidenceStrengthBadge } from '../components/TourEvidencePanel';
import { useTourData } from '../data/tour-data';

export function TourInvestigationPage() {
  const fixture = useTourData();
  const inv = fixture.investigation;
  const story = fixture.collaborative_investigation_example;
  const measurementMoment = story.moments.find((moment) => moment.sequence === 4) ?? story.moments[0]!;
  const historyMoment = story.moments.find((moment) => moment.sequence === 3) ?? story.moments[0]!;
  const [focused, setFocused] = useState(false);
  const [claims, setClaims] = useState(inv.findings[0]?.evidence_claim_ids ?? []);
  const [evidenceTitle, setEvidenceTitle] = useState('Evidence supporting the current Investigation');

  const synthesis = useMemo<InvestigationSynthesisDto>(() => ({
    status: 'MATERIAL_CHANGE',
    material_change: true,
    summary: measurementMoment.route42_contribution,
    changes: [
      { change_type: 'POSSIBLE_CAUSE_STRENGTHENED', message: 'Suction restriction is now more strongly supported.' },
      { change_type: 'UNCERTAINTY_CHANGED', message: 'Internal wear remains plausible, so root cause is not yet confirmed.' },
      { change_type: 'WHERE_THINGS_STAND_UPDATED', message: 'The shared assessment now reflects James\'s field measurement.' },
    ],
    uncertainty: 'The new measurement strengthens one possible cause, but the team still needs the post-action operating result before confirming the cause.',
    suggested_next_check: measurementMoment.next_action,
    assessment_updated: true,
    source_kind: 'FIELD_MEASUREMENT',
    source_id: measurementMoment.moment_id,
    source_user_id: measurementMoment.primary_participant_id,
    reviewed_at: inv.updated_at,
  }), [inv.updated_at, measurementMoment]);

  function showEvidence(ids: string[], title: string) {
    setClaims(ids);
    setEvidenceTitle(title);
  }

  return (
    <section className={`r42-investigation-page r42-tour-investigation-page${focused ? ' r42-investigation-page--conversation-focus' : ''}`}>
      <header className="r42-investigation-head">
        <div>
          <span className="r42-eyebrow">{fixture.machine.machine_name} investigation</span>
          <h1>{inv.title}</h1>
          <p>{inv.objective}</p>
        </div>
        <div className="r42-investigation-head-actions">
          <span className="r42-investigation-status r42-investigation-status--open">Active</span>
          {!focused ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled>Close investigation</button> : null}
        </div>
      </header>

      <div className="r42-investigation-layout r42-tour-investigation-layout">
        <main className="r42-investigation-main">
          {!focused ? (
            <section className="r42-investigation-section r42-investigation-assessment" data-tour-target="investigation-assessment">
              <div className="r42-investigation-section-head">
                <div><span className="r42-eyebrow">Where things stand</span><h2>What the team currently understands</h2></div>
              </div>
              <p className="r42-investigation-assessment-text">{measurementMoment.understanding_after}</p>
            </section>
          ) : null}

          <div data-tour-target="investigation-reasoning" className="r42-tour-investigation-workflow">
            {!focused ? <div className="r42-tour-investigation-mobile-target" data-tour-target="investigation-reasoning-mobile"><InvestigationSynthesisCard synthesis={synthesis} /></div> : null}
            {!focused ? <InvestigationAiRoleStrip /> : null}

            <section className="r42-investigation-section r42-investigation-conversation" data-focus-mode={focused ? 'focused' : 'overview'}>
              <div className="r42-investigation-section-head r42-investigation-conversation-head">
                <div>
                  <span className="r42-eyebrow">Shared investigation</span>
                  <h2>{focused ? inv.title : 'Work the problem together'}</h2>
                </div>
                <button
                  type="button"
                  className="r42-investigation-focus-toggle"
                  onClick={() => setFocused((value) => !value)}
                  aria-pressed={focused}
                  aria-label={focused ? 'Back to investigation' : 'Focus Investigation conversation'}
                >
                  <i className={`bi ${focused ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'}`} aria-hidden="true" />
                  <span>{focused ? 'Back to investigation' : 'Focus conversation'}</span>
                </button>
              </div>

              {focused ? (
                <div className="r42-investigation-focus-context" aria-label="Investigation focus context">
                  <span className="r42-eyebrow">Where things stand</span>
                  <p>{measurementMoment.understanding_after}</p>
                </div>
              ) : null}

              <div className="r42-investigation-turns r42-tour-investigation-turns" aria-label="Example shared Investigation thread">
                <article className="r42-investigation-thread-entry">
                  <div className="r42-investigation-team-entry">
                    <div className="r42-investigation-team-avatar" aria-hidden="true">MO</div>
                    <div className="r42-investigation-team-entry__body">
                      <div className="r42-investigation-team-entry__meta">
                        <strong>Maria</strong><span>Operator</span><time>10:18 AM</time><span className="r42-investigation-asked-ace">Asked Ace</span>
                      </div>
                      <p className="r42-investigation-team-entry__text">Has P-101 shown this kind of hydraulic problem before?</p>
                    </div>
                  </div>
                  <div className="r42-investigation-ace-entry">
                    <div className="r42-investigation-ace-entry__meta">
                      <span className="r42-investigation-ace-avatar"><i className="bi bi-compass" aria-hidden="true" /></span>
                      <strong>Ace</strong><span>Shared reasoning</span>
                    </div>
                    <p>{historyMoment.route42_contribution}</p>
                    <button
                      type="button"
                      className="r42-tour-evidence-button"
                      onClick={() => showEvidence(historyMoment.evidence.flatMap((item) => item.source_claim_ids), 'Evidence Route42 connected in the Investigation')}
                    >
                      <i className="bi bi-journal-check" aria-hidden="true" /> View supporting evidence
                    </button>
                  </div>
                </article>
              </div>

              <div className="r42-tour-composer-mock r42-tour-investigation-composer" aria-label="Read-only Investigation composer">
                <span>Add an update or ask a question…</span>
                <div className="r42-tour-investigation-composer__actions">
                  <button type="button" disabled>Post update</button>
                  <button type="button" disabled>Ask Ace</button>
                </div>
                <small>The guided example is read-only. In your workspace, post updates for the team or ask Ace when Route42 reasoning would help.</small>
              </div>
            </section>
          </div>

          {!focused ? (
            <>
              <InvestigationKnowledgeSection
                eyebrow="Shared understanding"
                title="What we know"
                count={inv.findings.length}
                summary={`${inv.findings.length} things the team has recorded.`}
                emptyText="Nothing has been recorded as known yet."
              >
                <div className="r42-investigation-finding-list">
                  {inv.findings.map((item) => (
                    <button key={item.finding_id} type="button" className="r42-tour-investigation-item" onClick={() => showEvidence(item.evidence_claim_ids, `Evidence for “${item.title}”`)}>
                      <div><span className="r42-eyebrow">What we know</span><strong>{item.title}</strong><p>{item.statement}</p></div>
                      <TourEvidenceStrengthBadge strength={item.evidence_strength} />
                    </button>
                  ))}
                </div>
              </InvestigationKnowledgeSection>

              <InvestigationKnowledgeSection
                eyebrow="Working explanations"
                title="Possible causes"
                definition="Things that might explain the problem. They are not confirmed yet."
                count={inv.hypotheses.length}
                summary="Suction restriction is currently the strongest lead; other causes remain possible."
                emptyText="No possible causes are being tracked yet."
              >
                <div className="r42-investigation-finding-list">
                  {inv.hypotheses.map((item) => (
                    <button key={item.hypothesis_id} type="button" className="r42-tour-investigation-item r42-tour-investigation-item--hypothesis" onClick={() => showEvidence(item.supporting_evidence_claim_ids, `Evidence for “${item.title}”`)}>
                      <div><span className="r42-eyebrow">{item.status === 'POSSIBLE_UNTESTED' ? 'Possible · not tested yet' : 'Possible · not confirmed'}</span><strong>{item.title}</strong><p>{item.limitation}</p></div>
                      <TourEvidenceStrengthBadge strength={item.evidence_strength} />
                    </button>
                  ))}
                </div>
              </InvestigationKnowledgeSection>

              <InvestigationKnowledgeSection
                eyebrow="Next checks"
                title="What we still need to check"
                definition="Information that could change the team's understanding."
                count={inv.open_questions.length}
                summary={`Next: ${inv.open_questions[1] ?? inv.open_questions[0] ?? 'No open check remains.'}`}
                emptyText="Nothing is waiting to be checked right now."
              >
                <ul className="r42-investigation-questions">{inv.open_questions.map((question) => <li key={question}>{question}</li>)}</ul>
              </InvestigationKnowledgeSection>

              <div className="r42-tour-investigation-learn-link">
                <i className="bi bi-people" aria-hidden="true" />
                <div><strong>Want to see the full team workflow?</strong><span>Follow three workers and Route42 from first observation through resolution.</span></div>
                <Link to="/learn/investigations">See an Investigation in action</Link>
              </div>
              <div className="r42-tour-next-card">
                <div><strong>The team contributes. Route42 keeps the understanding current.</strong><span>When the Investigation closes, evidence-backed learning can carry forward into Machine Memory.</span></div>
                <Link className="btn r42-btn-primary" to="/tour/memory">See Machine Memory</Link>
              </div>
            </>
          ) : null}
        </main>

        {!focused ? (
          <aside className="r42-tour-investigation-context-stack" aria-label="Investigation context and example evidence">
            <div className="r42-investigation-context">
              <section><span className="r42-eyebrow">What we're trying to figure out</span><p>{inv.objective}</p></section>
              <section>
                <span className="r42-eyebrow">What supports this</span>
                <strong>{inv.pinned_document_ids.length} pinned</strong>
                <p>Manual guidance, maintenance history, and the current panel image stay attached to this Investigation.</p>
              </section>
              <section><span className="r42-eyebrow">Team</span><strong>3</strong><p>Maria · Operator<br />James · Maintenance Technician<br />Danielle · Reliability Engineer</p></section>
              <section><span className="r42-eyebrow">Started from</span><Link to="/tour/conversation">Open source conversation <i className="bi bi-arrow-up-right" /></Link></section>
            </div>
            <TourEvidencePanel fixture={fixture} claimIds={claims} title={evidenceTitle} />
          </aside>
        ) : null}
      </div>
    </section>
  );
}
